
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

// ─── Вкладки ─────────────────────────────────────────────────────────────────
const TABS = [
  { key: 'compare',  label: 'Сравнение цен',  icon: '⚖️' },
  { key: 'upload',   label: 'Загрузить прайс', icon: '📤' },
  { key: 'alerts',   label: 'Алерты',          icon: '🔔' },
  { key: 'history',  label: 'История загрузок', icon: '📁' },
  { key: 'matches',  label: 'Матчинг',          icon: '🔗' },
  { key: 'suppliers',label: 'Поставщики',       icon: '🏭' },
  { key: 'sources',  label: 'Источники прайсов', icon: '🔌' },
];

// ─── Главный компонент ────────────────────────────────────────────────────────
export default function PriceMonitor() {
  const [tab, setTab]               = useState('compare');
  const [suppliers, setSuppliers]   = useState([]);
  const [products, setProducts]     = useState([]);
  const [compare, setCompare]       = useState([]);
  const [alerts, setAlerts]         = useState([]);
  const [uploads, setUploads]       = useState([]);
  const [matches, setMatches]       = useState([]);
  const [loading, setLoading]       = useState({});
  const [alertCount, setAlertCount] = useState(0);

  const setLoad = (key, val) => setLoading(p => ({ ...p, [key]: val }));

  useEffect(() => {
    axios.get('/api/price-monitor/suppliers').then(r => setSuppliers(r.data)).catch(() => {});
    axios.get('/api/price-monitor/products').then(r => setProducts(r.data)).catch(() => {});
    axios.get('/api/price-monitor/alerts').then(r => {
      setAlerts(r.data);
      setAlertCount(r.data.length);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (tab === 'compare') {
      setLoad('compare', true);
      axios.get('/api/price-monitor/prices/compare')
        .then(r => setCompare(r.data))
        .finally(() => setLoad('compare', false));
    }
    if (tab === 'history') {
      setLoad('history', true);
      axios.get('/api/price-monitor/uploads')
        .then(r => setUploads(r.data))
        .finally(() => setLoad('history', false));
    }
    if (tab === 'matches') {
      setLoad('matches', true);
      axios.get('/api/price-monitor/matches/pending')
        .then(r => setMatches(r.data))
        .finally(() => setLoad('matches', false));
    }
  }, [tab]);

  return (
    <div>
      <div className="page-header">
        <div className="page-title">💰 Мониторинг цен поставщиков</div>
        <div className="page-subtitle">
          {suppliers.length} поставщиков · {products.length} позиций номенклатуры
          {alertCount > 0 && (
            <span style={{ marginLeft: 12, background: '#FEE2E2', color: '#991B1B',
              padding: '2px 10px', borderRadius: 20, fontSize: 12, fontWeight: 600 }}>
              🔔 {alertCount} алертов
            </span>
          )}
        </div>
      </div>

      {/* Вкладки */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 24,
        borderBottom: '1px solid #E8EAF0', paddingBottom: 0 }}>
        {TABS.map(t => (
          <button key={t.key} onClick={() => setTab(t.key)} style={{
            padding: '10px 18px', border: '2px solid transparent',
            borderBottom: tab === t.key ? '2px solid #10B981' : '2px solid transparent',
            borderRadius: 0, background: 'none', cursor: 'pointer',
            fontSize: 14, fontWeight: tab === t.key ? 600 : 400,
            color: tab === t.key ? '#10B981' : '#6B7280',
            position: 'relative', top: 1,
          }}>
            {t.icon} {t.label}
            {t.key === 'alerts' && alertCount > 0 && (
              <span style={{ marginLeft: 6, background: '#EF4444', color: '#fff',
                borderRadius: 10, padding: '1px 6px', fontSize: 11 }}>
                {alertCount}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Контент вкладок */}
      {tab === 'compare'   && <TabCompare data={compare} loading={loading.compare} suppliers={suppliers} />}
      {tab === 'upload'    && <TabUpload suppliers={suppliers} onSuccess={() => setTab('compare')} />}
      {tab === 'alerts'    && <TabAlerts alerts={alerts} setAlerts={setAlerts} setAlertCount={setAlertCount} />}
      {tab === 'history'   && <TabHistory uploads={uploads} loading={loading.history} />}
      {tab === 'matches'   && <TabMatches matches={matches} setMatches={setMatches} products={products} />}
      {tab === 'suppliers' && <TabSuppliers suppliers={suppliers} setSuppliers={setSuppliers} />}
      {tab === 'sources'   && <TabSources suppliers={suppliers} />}
    </div>
  );
}

// ─── Вкладка: Сравнение цен ───────────────────────────────────────────────────
function TabCompare({ data, loading, suppliers }) {
  const [search, setSearch] = useState('');
  const [filterSupplier, setFilterSupplier] = useState('');

  const filtered = data.filter(p => {
    const matchSearch = p.name.toLowerCase().includes(search.toLowerCase());
    const matchSup = !filterSupplier || p.suppliers.some(s => s.supplier_id == filterSupplier);
    return matchSearch && matchSup;
  });

  if (loading) return (
    <div style={{ textAlign: 'center', padding: 60, color: '#8892A4' }}>
      Загружаем данные...
    </div>
  );

  if (!data.length) return (
    <div className="card" style={{ textAlign: 'center', padding: 60 }}>
      <div style={{ fontSize: 48, marginBottom: 16 }}>📊</div>
      <div style={{ fontSize: 18, fontWeight: 600, color: '#1a1a2e', marginBottom: 8 }}>
        Нет данных для сравнения
      </div>
      <div style={{ color: '#8892A4', marginBottom: 24 }}>
        Загрузите прайс от поставщика чтобы увидеть сравнение цен
      </div>
      <div style={{ color: '#10B981', fontSize: 14 }}>
        → Перейдите на вкладку «Загрузить прайс»
      </div>
    </div>
  );

  // Собираем список всех поставщиков из данных
  const allSuppliers = [];
  const seen = new Set();
  data.forEach(p => p.suppliers.forEach(s => {
    if (!seen.has(s.supplier_id)) {
      seen.add(s.supplier_id);
      allSuppliers.push({ id: s.supplier_id, name: s.supplier_name });
    }
  }));

  return (
    <div>
      {/* Фильтры */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 20 }}>
        <input
          placeholder="🔍 Поиск по позиции..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{ flex: 1, padding: '10px 16px', border: '1px solid #E8EAF0',
            borderRadius: 8, fontSize: 14, outline: 'none' }}
        />
        <select
          value={filterSupplier}
          onChange={e => setFilterSupplier(e.target.value)}
          style={{ padding: '10px 16px', border: '1px solid #E8EAF0',
            borderRadius: 8, fontSize: 14, outline: 'none', minWidth: 180 }}
        >
          <option value="">Все поставщики</option>
          {allSuppliers.map(s => (
            <option key={s.id} value={s.id}>{s.name}</option>
          ))}
        </select>
      </div>

      {/* Итоговые метрики */}
      <div className="metrics-grid" style={{ marginBottom: 20 }}>
        <div className="metric-card success">
          <div className="metric-value">{filtered.length}</div>
          <div className="metric-label">Позиций в сравнении</div>
        </div>
        <div className="metric-card">
          <div className="metric-value">{allSuppliers.length}</div>
          <div className="metric-label">Поставщиков</div>
        </div>
        <div className="metric-card warning">
          <div className="metric-value">
            {filtered.filter(p => p.suppliers.length > 1).length}
          </div>
          <div className="metric-label">Есть альтернатива дешевле</div>
        </div>
      </div>

      {/* Таблица сравнения */}
      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        <table className="table">
          <thead>
            <tr>
              <th style={{ padding: '14px 20px' }}>Позиция</th>
              <th>Ед.</th>
              {allSuppliers.map(s => (
                <th key={s.id} style={{ textAlign: 'right' }}>{s.name}</th>
              ))}
              <th style={{ textAlign: 'right' }}>Лучшая цена</th>
              <th style={{ textAlign: 'right' }}>Экономия</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map(product => {
              const bestPrice = Math.min(...product.suppliers.map(s => s.price));
              const worstPrice = Math.max(...product.suppliers.map(s => s.price));
              const saving = product.suppliers.length > 1
                ? ((worstPrice - bestPrice) / worstPrice * 100).toFixed(1)
                : null;

              return (
                <tr key={product.id} style={{ transition: 'background 0.15s' }}
                  onMouseEnter={e => e.currentTarget.style.background = '#F9FAFB'}
                  onMouseLeave={e => e.currentTarget.style.background = ''}>
                  <td style={{ padding: '12px 20px', fontWeight: 500, minWidth: 220, color: '#1a1a2e' }}>{product.name}</td>
                  <td style={{ color: '#8892A4', fontSize: 13 }}>{product.unit}</td>
                  {allSuppliers.map(sup => {
                    const entry = product.suppliers.find(s => s.supplier_id === sup.id);
                    if (!entry) return <td key={sup.id} style={{ textAlign: 'right', color: '#E8EAF0' }}>—</td>;
                    const isBest = entry.price === bestPrice;
                    const isWorst = entry.price === worstPrice && product.suppliers.length > 1;
                    return (
                      <td key={sup.id} style={{ textAlign: 'right',
                        color: isBest ? '#065F46' : isWorst ? '#991B1B' : '#1a1a2e',
                        fontWeight: isBest ? 700 : 400,
                        background: isBest ? '#F0FDF4' : isWorst ? '#FFF5F5' : 'transparent' }}>
                        {entry.price.toLocaleString('ru')} ₽
                        <div style={{ fontSize: 11, color: '#8892A4', fontWeight: 400 }}>
                          {entry.price_date}
                        </div>
                      </td>
                    );
                  })}
                  <td style={{ textAlign: 'right', fontWeight: 700, color: '#065F46' }}>
                    {bestPrice.toLocaleString('ru')} ₽
                  </td>
                  <td style={{ textAlign: 'right' }}>
                    {saving ? (
                      <span style={{ background: '#D1FAE5', color: '#065F46',
                        padding: '3px 8px', borderRadius: 12, fontSize: 12, fontWeight: 600 }}>
                        -{saving}%
                      </span>
                    ) : <span style={{ color: '#E8EAF0' }}>—</span>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── Вкладка: Загрузка прайса ────────────────────────────────────────────────
function TabUpload({ suppliers, onSuccess }) {
  const [supplierId, setSupplierId] = useState('');
  const [priceDate, setPriceDate]   = useState(new Date().toISOString().slice(0, 10));
  const [file, setFile]             = useState(null);
  const [loading, setLoading]       = useState(false);
  const [result, setResult]         = useState(null);
  const [error, setError]           = useState('');

  const handleSubmit = async () => {
    if (!supplierId || !file || !priceDate) {
      setError('Заполните все поля');
      return;
    }
    setLoading(true);
    setError('');
    setResult(null);
    const fd = new FormData();
    fd.append('file', file);
    fd.append('supplier_id', supplierId);
    fd.append('price_date', priceDate);
    try {
      const r = await axios.post('/api/price-monitor/upload', fd);
      setResult(r.data);
    } catch (e) {
      setError(e.response?.data?.detail || 'Ошибка загрузки');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ maxWidth: 600 }}>
      <div className="card">
        <div className="card-title">📤 Загрузить прайс поставщика</div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {/* Поставщик */}
          <div>
            <label style={{ fontSize: 13, fontWeight: 500, color: '#374151', display: 'block', marginBottom: 6 }}>
              Поставщик
            </label>
            <select value={supplierId} onChange={e => setSupplierId(e.target.value)}
              style={{ width: '100%', padding: '10px 14px', border: '1px solid #E8EAF0',
                borderRadius: 8, fontSize: 14, outline: 'none' }}>
              <option value="">Выберите поставщика...</option>
              {suppliers.map(s => (
                <option key={s.id} value={s.id}>{s.name}</option>
              ))}
            </select>
          </div>

          {/* Дата прайса */}
          <div>
            <label style={{ fontSize: 13, fontWeight: 500, color: '#374151', display: 'block', marginBottom: 6 }}>
              Дата прайса
            </label>
            <input type="date" value={priceDate} onChange={e => setPriceDate(e.target.value)}
              style={{ width: '100%', padding: '10px 14px', border: '1px solid #E8EAF0',
                borderRadius: 8, fontSize: 14, outline: 'none' }} />
          </div>

          {/* Файл */}
          <div>
            <label style={{ fontSize: 13, fontWeight: 500, color: '#374151', display: 'block', marginBottom: 6 }}>
              Файл прайса (PDF, Excel, CSV)
            </label>
            <div
              onClick={() => document.getElementById('price-file').click()}
              style={{ border: '2px dashed #E8EAF0', borderRadius: 8, padding: '32px 24px',
                textAlign: 'center', cursor: 'pointer', transition: 'border-color 0.2s',
                background: file ? '#F0FDF4' : '#FAFAFA' }}
              onDragOver={e => e.preventDefault()}
              onDrop={e => { e.preventDefault(); setFile(e.dataTransfer.files[0]); }}>
              <input id="price-file" type="file"
                accept=".pdf,.xlsx,.xls,.csv" style={{ display: 'none' }}
                onChange={e => setFile(e.target.files[0])} />
              {file ? (
                <div>
                  <div style={{ fontSize: 24, marginBottom: 8 }}>✅</div>
                  <div style={{ fontWeight: 600, color: '#065F46' }}>{file.name}</div>
                  <div style={{ fontSize: 12, color: '#8892A4', marginTop: 4 }}>
                    {(file.size / 1024).toFixed(0)} KB · нажмите чтобы заменить
                  </div>
                </div>
              ) : (
                <div>
                  <div style={{ fontSize: 32, marginBottom: 8 }}>📂</div>
                  <div style={{ color: '#6B7280' }}>Перетащите файл или нажмите для выбора</div>
                  <div style={{ fontSize: 12, color: '#8892A4', marginTop: 4 }}>PDF, XLSX, XLS, CSV</div>
                </div>
              )}
            </div>
          </div>

          {error && (
            <div style={{ background: '#FEE2E2', color: '#991B1B',
              padding: '10px 14px', borderRadius: 8, fontSize: 14 }}>
              ⚠️ {error}
            </div>
          )}

          <button onClick={handleSubmit} disabled={loading}
            style={{ padding: '12px 24px', background: loading ? '#9CA3AF' : '#10B981',
              color: '#fff', border: 'none', borderRadius: 8, fontSize: 15,
              fontWeight: 600, cursor: loading ? 'not-allowed' : 'pointer' }}>
            {loading ? '⏳ Обрабатываем...' : '⬆️ Загрузить и обработать'}
          </button>

          {loading && (
            <div style={{ fontSize: 13, color: '#8892A4', textAlign: 'center' }}>
              Qwen анализирует позиции и сопоставляет с номенклатурой...
            </div>
          )}
        </div>
      </div>

      {/* Результат загрузки */}
      {result && (
        <div className="card" style={{ borderLeft: '4px solid #10B981' }}>
          <div className="card-title">✅ Прайс обработан</div>
          <div className="metrics-grid">
            <div className="metric-card">
              <div className="metric-value">{result.stats.total}</div>
              <div className="metric-label">Всего позиций</div>
            </div>
            <div className="metric-card success">
              <div className="metric-value">{result.stats.matched}</div>
              <div className="metric-label">Сматчено</div>
            </div>
            <div className="metric-card warning">
              <div className="metric-value">{result.stats.unmatched}</div>
              <div className="metric-label">Не сматчено</div>
            </div>
            <div className="metric-card danger">
              <div className="metric-value">{result.stats.alerts}</div>
              <div className="metric-label">Алертов роста цен</div>
            </div>
          </div>
          {result.stats.unmatched > 0 && (
            <div style={{ fontSize: 13, color: '#92400E', background: '#FEF3C7',
              padding: '10px 14px', borderRadius: 8, marginTop: 8 }}>
              ⚠️ {result.stats.unmatched} позиций не сматчено — проверьте вкладку «Матчинг»
            </div>
          )}
          <button onClick={onSuccess}
            style={{ marginTop: 16, padding: '10px 20px', background: '#F0FDF4',
              color: '#065F46', border: '1px solid #D1FAE5', borderRadius: 8,
              fontSize: 14, fontWeight: 500, cursor: 'pointer' }}>
            → Посмотреть сравнение цен
          </button>
        </div>
      )}
    </div>
  );
}

// ─── Вкладка: Алерты ─────────────────────────────────────────────────────────
function TabAlerts({ alerts, setAlerts, setAlertCount }) {
  const markSeen = async (id) => {
    await axios.patch(`/api/price-monitor/alerts/${id}/seen`);
    const updated = alerts.filter(a => a.id !== id);
    setAlerts(updated);
    setAlertCount(updated.length);
  };

  if (!alerts.length) return (
    <div className="card" style={{ textAlign: 'center', padding: 60 }}>
      <div style={{ fontSize: 48, marginBottom: 16 }}>✅</div>
      <div style={{ fontSize: 18, fontWeight: 600, color: '#1a1a2e', marginBottom: 8 }}>
        Нет новых алертов
      </div>
      <div style={{ color: '#8892A4' }}>
        Система следит за ценами и уведомит при росте больше 3%
      </div>
    </div>
  );

  return (
    <div>
      <div style={{ marginBottom: 16, fontSize: 14, color: '#8892A4' }}>
        Показаны позиции где цена выросла более чем на 3% относительно предыдущего прайса
      </div>
      {alerts.map(a => (
        <div key={a.id} className="card"
          style={{ borderLeft: `4px solid ${a.change_pct > 10 ? '#EF4444' : '#F59E0B'}`,
            display: 'flex', alignItems: 'center', gap: 20, marginBottom: 12 }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 600, fontSize: 15, marginBottom: 4, color: '#1a1a2e' }}>{a.product}</div>
            <div style={{ fontSize: 13, color: '#8892A4' }}>
              {a.supplier} · {a.prev_date} → {a.new_date}
            </div>
          </div>
          <div style={{ textAlign: 'right', minWidth: 140 }}>
            <div style={{ fontSize: 13, color: '#8892A4', textDecoration: 'line-through' }}>
              {a.prev_price.toLocaleString('ru')} ₽
            </div>
            <div style={{ fontSize: 18, fontWeight: 700, color: '#EF4444' }}>
              {a.new_price.toLocaleString('ru')} ₽
            </div>
          </div>
          <div style={{ minWidth: 80, textAlign: 'center' }}>
            <span style={{ background: '#FEE2E2', color: '#991B1B',
              padding: '6px 12px', borderRadius: 20, fontSize: 14, fontWeight: 700 }}>
              +{a.change_pct}%
            </span>
          </div>
          <button onClick={() => markSeen(a.id)}
            style={{ padding: '8px 16px', background: '#F3F4F6', border: 'none',
              borderRadius: 8, fontSize: 13, cursor: 'pointer', color: '#6B7280',
              whiteSpace: 'nowrap' }}>
            Принято ✓
          </button>
        </div>
      ))}
    </div>
  );
}

// ─── Вкладка: История загрузок ───────────────────────────────────────────────
function TabHistory({ uploads, loading }) {
  if (loading) return (
    <div style={{ textAlign: 'center', padding: 60, color: '#8892A4' }}>Загружаем...</div>
  );
  if (!uploads.length) return (
    <div className="card" style={{ textAlign: 'center', padding: 60 }}>
      <div style={{ fontSize: 48, marginBottom: 16 }}>📁</div>
      <div style={{ fontSize: 18, fontWeight: 600, color: '#1a1a2e' }}>
        Загрузок пока нет
      </div>
    </div>
  );
  return (
    <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
      <table className="table">
        <thead>
          <tr>
            <th style={{ padding: '14px 20px' }}>Файл</th>
            <th>Поставщик</th>
            <th>Дата прайса</th>
            <th style={{ textAlign: 'right' }}>Позиций</th>
            <th style={{ textAlign: 'right' }}>Сматчено</th>
            <th style={{ textAlign: 'right' }}>Не сматчено</th>
            <th>Статус</th>
            <th>Загружено</th>
          </tr>
        </thead>
        <tbody>
          {uploads.map(u => (
            <tr key={u.id}>
              <td style={{ padding: '12px 20px', fontWeight: 500, maxWidth: 200,
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: '#111827' }}>
                {u.filename}
              </td>
              <td style={{ color: '#111827' }}>{u.supplier || '—'}</td>
              <td style={{ color: '#374151' }}>{u.price_date}</td>
              <td style={{ textAlign: 'right', color: '#111827' }}>{u.total}</td>
              <td style={{ textAlign: 'right', color: '#065F46', fontWeight: 600 }}>
                {u.matched}
              </td>
              <td style={{ textAlign: 'right',
                color: u.unmatched > 0 ? '#92400E' : '#8892A4', fontWeight: u.unmatched > 0 ? 600 : 400 }}>
                {u.unmatched}
              </td>
              <td>
                <span className={`badge ${u.status === 'done' ? 'badge-success' : 'badge-warning'}`}>
                  {u.status === 'done' ? '✓ Готово' : u.status}
                </span>
              </td>
              <td style={{ fontSize: 12, color: '#8892A4' }}>
                {new Date(u.uploaded_at).toLocaleString('ru')}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ─── Вкладка: Матчинг ────────────────────────────────────────────────────────
function TabMatches({ matches, setMatches, products }) {
  const confirm = async (id) => {
    await axios.patch(`/api/price-monitor/matches/${id}/confirm`);
    setMatches(prev => prev.filter(m => m.id !== id));
  };
  const reject = async (id) => {
    await axios.patch(`/api/price-monitor/matches/${id}/reject`);
    setMatches(prev => prev.filter(m => m.id !== id));
  };

  if (!matches.length) return (
    <div className="card" style={{ textAlign: 'center', padding: 60 }}>
      <div style={{ fontSize: 48, marginBottom: 16 }}>🔗</div>
      <div style={{ fontSize: 18, fontWeight: 600, color: '#1a1a2e', marginBottom: 8 }}>
        Нет матчей на подтверждение
      </div>
      <div style={{ color: '#8892A4' }}>
        Все позиции сопоставлены. При следующей загрузке Qwen не будет спрашивать повторно.
      </div>
    </div>
  );

  return (
    <div>
      <div style={{ marginBottom: 16, fontSize: 14, color: '#8892A4' }}>
        Проверьте сопоставления Qwen и подтвердите или исправьте.
        Подтверждённые матчи кэшируются — повторно Qwen не вызывается.
      </div>
      {matches.map(m => (
        <div key={m.id} className="card"
          style={{ display: 'flex', alignItems: 'center', gap: 16,
            marginBottom: 10, padding: '16px 20px' }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 12, color: '#374151', marginBottom: 4 }}>
              {m.supplier} · арт. {m.article || '—'}
            </div>
            <div style={{ fontWeight: 500, color: '#1a1a2e' }}>{m.supplier_name}</div>
          </div>
          <div style={{ color: '#8892A4', fontSize: 20 }}>→</div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 12, color: '#8892A4', marginBottom: 4 }}>
              Наша номенклатура
            </div>
            <div style={{ fontWeight: 600, color: '#1a1a2e' }}>{m.product}</div>
          </div>
          <div style={{ minWidth: 70, textAlign: 'center' }}>
            <span style={{
              background: m.confidence >= 0.9 ? '#D1FAE5' : '#FEF3C7',
              color: m.confidence >= 0.9 ? '#065F46' : '#92400E',
              padding: '4px 10px', borderRadius: 12, fontSize: 12, fontWeight: 600
            }}>
              {Math.round(m.confidence * 100)}%
            </span>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={() => confirm(m.id)}
              style={{ padding: '8px 16px', background: '#D1FAE5', color: '#065F46',
                border: 'none', borderRadius: 8, fontSize: 13,
                fontWeight: 600, cursor: 'pointer' }}>
              ✓ Верно
            </button>
            <button onClick={() => reject(m.id)}
              style={{ padding: '8px 16px', background: '#FEE2E2', color: '#991B1B',
                border: 'none', borderRadius: 8, fontSize: 13,
                fontWeight: 600, cursor: 'pointer' }}>
              ✗ Неверно
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── Вкладка: Поставщики ─────────────────────────────────────────────────────
function TabSuppliers({ suppliers, setSuppliers }) {
  const [form, setForm]     = useState({ name: '', email: '', website: '', notes: '' });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState('');

  const handleAdd = async () => {
    if (!form.name.trim()) return;
    setLoading(true);
    try {
      const r = await axios.post('/api/price-monitor/suppliers', form);
      setSuppliers(prev => [...prev, { ...r.data, rating: 5, active: true }]);
      setForm({ name: '', email: '', website: '', notes: '' });
      setSuccess('Поставщик добавлен');
      setTimeout(() => setSuccess(''), 3000);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 340px', gap: 20 }}>
      {/* Список поставщиков */}
      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        <div style={{ padding: '16px 20px', borderBottom: '1px solid #E8EAF0',
          fontWeight: 600, fontSize: 15 }}>
          Поставщики ({suppliers.length})
        </div>
        <table className="table">
          <thead>
            <tr>
              <th style={{ padding: '12px 20px' }}>Название</th>
              <th>Email</th>
              <th>Сайт</th>
              <th style={{ textAlign: 'center' }}>Рейтинг</th>
            </tr>
          </thead>
          <tbody>
            {suppliers.map(s => (
              <tr key={s.id}>
                <td style={{ padding: '12px 20px', fontWeight: 500, color: '#111827' }}>{s.name}</td>
                <td style={{ fontSize: 13, color: '#374151' }}>{s.email || '—'}</td>
                <td style={{ fontSize: 13 }}>
                  {s.website
                    ? <a href={`https://${s.website}`} target="_blank" rel="noreferrer"
                        style={{ color: '#10B981' }}>{s.website}</a>
                    : '—'}
                </td>
                <td style={{ textAlign: 'center' }}>
                  {'★'.repeat(Math.round(s.rating))}{'☆'.repeat(5 - Math.round(s.rating))}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Форма добавления */}
      <div className="card">
        <div className="card-title">+ Новый поставщик</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {[
            { key: 'name',    label: 'Название *', placeholder: 'Комус' },
            { key: 'email',   label: 'Email',       placeholder: 'kc@komus.net' },
            { key: 'website', label: 'Сайт',        placeholder: 'komus.ru' },
            { key: 'notes',   label: 'Заметки',     placeholder: 'Канцтовары и хозтовары' },
          ].map(f => (
            <div key={f.key}>
              <label style={{ fontSize: 12, fontWeight: 500, color: '#6B7280',
                display: 'block', marginBottom: 4 }}>{f.label}</label>
              <input
                value={form[f.key]}
                onChange={e => setForm(p => ({ ...p, [f.key]: e.target.value }))}
                placeholder={f.placeholder}
                style={{ width: '100%', padding: '9px 12px', border: '1px solid #E8EAF0',
                  borderRadius: 8, fontSize: 14, outline: 'none' }}
              />
            </div>
          ))}
          {success && (
            <div style={{ background: '#D1FAE5', color: '#065F46',
              padding: '8px 12px', borderRadius: 8, fontSize: 13 }}>
              ✓ {success}
            </div>
          )}
          <button onClick={handleAdd} disabled={loading || !form.name.trim()}
            style={{ padding: '10px', background: '#10B981', color: '#fff',
              border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 600,
              cursor: 'pointer', opacity: loading || !form.name.trim() ? 0.6 : 1 }}>
            {loading ? 'Сохраняем...' : 'Добавить поставщика'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Вкладка: Источники прайсов ──────────────────────────────────────────────
function TabSources({ suppliers }) {
  const [checking, setChecking] = useState(false);
  const [checkResult, setCheckResult] = useState(null);

  const checkEmail = async () => {
    setChecking(true);
    setCheckResult(null);
    try {
      const r = await axios.post('/api/price-monitor/email/run');
      setCheckResult(r.data.result);
    } catch(e) {
      setCheckResult({ error: 'Ошибка подключения' });
    } finally {
      setChecking(false);
    }
  };

  const [sources, setSources]   = useState([]);
  const [showForm, setShowForm] = useState(null); // 'imap' | 'telegram' | 'manual'
  const [form, setForm]         = useState({});
  const [loading, setLoading]   = useState(false);
  const [saved, setSaved]       = useState('');

  useEffect(() => {
    axios.get('/api/price-monitor/sources').then(r => setSources(r.data)).catch(() => {});
  }, []);

  const SOURCE_TYPES = [
    {
      key: 'manual',
      icon: '📂',
      title: 'Ручная загрузка',
      desc: 'Загружайте PDF и Excel прайсы вручную через вкладку «Загрузить прайс»',
      color: '#10B981',
      bg: '#F0FDF4',
      active: true,
      fields: [],
    },
    {
      key: 'imap',
      icon: '📧',
      title: 'Email (IMAP)',
      desc: 'Агент проверяет почту и автоматически подхватывает прайсы из вложений',
      color: '#3B82F6',
      bg: '#EFF6FF',
      fields: [
        { key: 'provider', label: 'Провайдер', type: 'select', options: [
            { value: 'yandex', label: 'Яндекс 360', host: 'imap.yandex.ru', port: '993' },
            { value: 'mailru', label: 'Mail.ru', host: 'imap.mail.ru', port: '993' },
            { value: 'gmail',  label: 'Gmail', host: 'imap.gmail.com', port: '993' },
            { value: 'custom', label: 'Другой сервер', host: '', port: '993' },
        ]},
        { key: 'host',     label: 'IMAP сервер',  placeholder: 'imap.yandex.ru' },
        { key: 'port',     label: 'Порт',         placeholder: '993' },
        { key: 'login',    label: 'Email',        placeholder: 'zakupki@nomera.ru' },
        { key: 'password', label: 'Пароль приложения', placeholder: '••••••••', type: 'password' },
        { key: 'folder',   label: 'Папка',        placeholder: 'INBOX' },
      ],
      hint: true,
    },
    {
      key: 'telegram',
      icon: '✈️',
      title: 'Telegram бот',
      desc: 'Перешлите прайс боту — он автоматически обработает файл',
      color: '#0088CC',
      bg: '#F0F8FF',
      fields: [
        { key: 'bot_token', label: 'Bot Token', placeholder: '123456:ABC-DEF...' },
        { key: 'chat_id',   label: 'Chat ID',   placeholder: '-100123456789' },
      ],
    },
    {
      key: 'vk',
      icon: '💬',
      title: 'VK / Max',
      desc: 'Получение прайсов через VK Мессенджер или Max',
      color: '#0077FF',
      bg: '#F0F4FF',
      fields: [
        { key: 'token',   label: 'API токен', placeholder: 'vk1.a.xxx...' },
        { key: 'peer_id', label: 'Peer ID',   placeholder: '2000000001' },
      ],
    },
    {
      key: 'bitrix',
      icon: '🔶',
      title: 'Bitrix24',
      desc: 'Файл в чате Bitrix24 автоматически попадает в обработку',
      color: '#FF5722',
      bg: '#FFF8F5',
      fields: [
        { key: 'chat_id', label: 'Chat ID', placeholder: '4798' },
      ],
    },
  ];

  const handleSave = async (type) => {
    setLoading(true);
    try {
      await axios.post('/api/price-monitor/sources', {
        source_type: type,
        name: SOURCE_TYPES.find(s => s.key === type)?.title,
        config: form,
        supplier_id: form.supplier_id || null,
      });
      const r = await axios.get('/api/price-monitor/sources');
      setSources(r.data);
      setShowForm(null);
      setForm({});
      setSaved('Источник подключён');
      setTimeout(() => setSaved(''), 3000);
    } catch(e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <div style={{ marginBottom: 20, fontSize: 14, color: '#8892A4' }}>
        Настройте источники откуда агент будет автоматически получать прайсы.
        Все источники ведут в один pipeline — файл обрабатывается одинаково.
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 20,
    padding: '16px 20px', background: '#fff', borderRadius: 12,
    border: '1px solid #E8EAF0' }}>
    <div style={{ flex: 1 }}>
      <div style={{ fontWeight: 600, fontSize: 14, color: '#1a1a2e', marginBottom: 4 }}>
        📧 managerOTA@yandex.ru
      </div>
      <div style={{ fontSize: 13, color: '#8892A4' }}>
        Проверяется автоматически каждые 30 минут · фильтр: прайс, price, цены
      </div>
    </div>
      <div style={{ fontSize: 13, color: '#065F46', background: '#D1FAE5',
        padding: '8px 14px', borderRadius: 8 }}>
        Писем: {checkResult?.emails ?? 0} · Файлов: {checkResult?.files ?? 0} · Сматчено: {checkResult?.matched ?? 0}
        {(checkResult?.alerts ?? 0) > 0 && <span style={{ color: '#991B1B' }}> · 🔔 {checkResult?.alerts} алертов</span>}
      </div>
    )}
    {checkResult?.error && (
      <div style={{ fontSize: 13, color: '#991B1B', background: '#FEE2E2',
        padding: '8px 14px', borderRadius: 8 }}>
        ⚠️ {checkResult.error}
      </div>
    )}
    <button onClick={checkEmail} disabled={checking}
      style={{ padding: '10px 20px', background: checking ? '#9CA3AF' : '#3B82F6',
        color: '#fff', border: 'none', borderRadius: 8, fontSize: 14,
        fontWeight: 600, cursor: checking ? 'not-allowed' : 'pointer',
        whiteSpace: 'nowrap' }}>
      {checking ? '⏳ Проверяем...' : '📬 Проверить сейчас'}
    </button>
  </div>

  {saved && (
        <div style={{ background: '#D1FAE5', color: '#065F46', padding: '12px 16px',
          borderRadius: 8, marginBottom: 16, fontSize: 14 }}>
          ✓ {saved}
        </div>
      )}

      {/* Карточки источников */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 16, marginBottom: 32 }}>
        {SOURCE_TYPES.map(src => {
          const connected = sources.filter(s => s.source_type === src.key);
          const isOpen = showForm === src.key;
          return (
            <div key={src.key} className="card"
              style={{ borderTop: `3px solid ${src.color}`, padding: 20 }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 12 }}>
                <div style={{ fontSize: 28 }}>{src.icon}</div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600, fontSize: 15, color: '#1a1a2e', marginBottom: 4 }}>
                    {src.title}
                  </div>
                  <div style={{ fontSize: 13, color: '#8892A4', lineHeight: 1.4 }}>
                    {src.desc}
                  </div>
                </div>
              </div>

              {/* Подключённые источники */}
              {connected.length > 0 && (
                <div style={{ marginBottom: 12 }}>
                  {connected.map(c => (
                    <div key={c.id} style={{ display: 'flex', alignItems: 'center',
                      gap: 8, padding: '6px 10px', background: src.bg,
                      borderRadius: 6, marginBottom: 4, fontSize: 13 }}>
                      <span style={{ color: src.color }}>●</span>
                      <span style={{ flex: 1, color: '#374151' }}>{c.name}</span>
                      {c.last_checked && (
                        <span style={{ fontSize: 11, color: '#8892A4' }}>
                          {new Date(c.last_checked).toLocaleDateString('ru')}
                        </span>
                      )}
                      <span style={{ fontSize: 11, color: c.is_active ? '#10B981' : '#EF4444' }}>
                        {c.is_active ? 'активен' : 'выкл'}
                      </span>
                    </div>
                  ))}
                </div>
              )}

              {/* Форма подключения */}
              {isOpen && src.fields.length > 0 && (
                <div style={{ borderTop: '1px solid #E8EAF0', paddingTop: 14, marginBottom: 14 }}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    <div>
                      <label style={{ fontSize: 12, color: '#6B7280', display: 'block', marginBottom: 4 }}>
                        Поставщик (необязательно)
                      </label>
                      <select value={form.supplier_id || ''}
                        onChange={e => setForm(p => ({ ...p, supplier_id: e.target.value }))}
                        style={{ width: '100%', padding: '8px 10px', border: '1px solid #E8EAF0',
                          borderRadius: 6, fontSize: 13, outline: 'none' }}>
                        <option value="">Определить автоматически</option>
                        {suppliers.map(s => (
                          <option key={s.id} value={s.id}>{s.name}</option>
                        ))}
                      </select>
                    </div>
                    {src.fields.map(f => (
                      <div key={f.key}>
                        <label style={{ fontSize: 12, color: '#6B7280', display: 'block', marginBottom: 4 }}>
                          {f.label}
                        </label>
                        {f.type === 'select' ? (
                          <select
                            value={form[f.key] || ''}
                            onChange={e => {
                              const opt = f.options.find(o => o.value === e.target.value);
                              setForm(p => ({
                                ...p,
                                [f.key]: e.target.value,
                                ...(opt?.host ? { host: opt.host, port: opt.port } : {})
                              }));
                            }}
                            style={{ width: '100%', padding: '8px 10px', border: '1px solid #E8EAF0',
                              borderRadius: 6, fontSize: 13, outline: 'none' }}>
                            <option value="">Выберите провайдера...</option>
                            {f.options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                          </select>
                        ) : (
                          <input
                            type={f.type || 'text'}
                            placeholder={f.placeholder}
                            value={form[f.key] || ''}
                            onChange={e => setForm(p => ({ ...p, [f.key]: e.target.value }))}
                            style={{ width: '100%', padding: '8px 10px', border: '1px solid #E8EAF0',
                              borderRadius: 6, fontSize: 13, outline: 'none' }}
                          />
                        )}
                      </div>
                    ))}

                    {/* Инструкция по паролю приложения */}
                    {src.hint && form.provider && (
                      <div style={{ background: '#EFF6FF', border: '1px solid #BFDBFE',
                        borderRadius: 8, padding: '12px 14px', fontSize: 12, color: '#1E40AF' }}>
                        {form.provider === 'yandex' && (
                          <div>
                            <div style={{ fontWeight: 600, marginBottom: 6 }}>
                              📋 Как получить пароль приложения Яндекс:
                            </div>
                            <div style={{ lineHeight: 1.7 }}>
                              1. Зайди на <a href="https://id.yandex.ru" target="_blank" rel="noreferrer"
                                style={{ color: '#2563EB' }}>id.yandex.ru</a><br/>
                              2. Безопасность → Пароли приложений<br/>
                              3. Создать пароль → выбери «Почта»<br/>
                              4. Скопируй и вставь пароль в поле выше<br/>
                              <span style={{ color: '#6B7280', marginTop: 4, display: 'block' }}>
                                ⚠️ Обычный пароль не подойдёт — только пароль приложения
                              </span>
                            </div>
                          </div>
                        )}
                        {form.provider === 'mailru' && (
                          <div>
                            <div style={{ fontWeight: 600, marginBottom: 6 }}>
                              📋 Как получить пароль приложения Mail.ru:
                            </div>
                            <div style={{ lineHeight: 1.7 }}>
                              1. Зайди в <a href="https://account.mail.ru/user/security" target="_blank" rel="noreferrer"
                                style={{ color: '#2563EB' }}>настройки безопасности</a><br/>
                              2. Пароли для внешних приложений<br/>
                              3. Добавить → название «Thoth AI»<br/>
                              4. Скопируй и вставь пароль в поле выше
                            </div>
                          </div>
                        )}
                        {form.provider === 'gmail' && (
                          <div>
                            <div style={{ fontWeight: 600, marginBottom: 6 }}>
                              📋 Как подключить Gmail:
                            </div>
                            <div style={{ lineHeight: 1.7 }}>
                              1. Включи двухфакторную аутентификацию в Google<br/>
                              2. Зайди в <a href="https://myaccount.google.com/apppasswords" target="_blank" rel="noreferrer"
                                style={{ color: '#2563EB' }}>Пароли приложений</a><br/>
                              3. Выбери «Почта» и устройство<br/>
                              4. Скопируй 16-значный пароль<br/>
                              <span style={{ color: '#6B7280', marginTop: 4, display: 'block' }}>
                                ⚠️ Также включи IMAP в настройках Gmail
                              </span>
                            </div>
                          </div>
                        )}
                        {form.provider === 'custom' && (
                          <div style={{ lineHeight: 1.7 }}>
                            Введи данные IMAP вручную. Обычно:<br/>
                            Порт 993 (SSL) или 143 (без SSL)
                          </div>
                        )}
                      </div>
                    )}
                    <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
                      <button onClick={() => handleSave(src.key)} disabled={loading}
                        style={{ flex: 1, padding: '9px', background: src.color,
                          color: '#fff', border: 'none', borderRadius: 6,
                          fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                        {loading ? 'Сохраняем...' : 'Подключить'}
                      </button>
                      <button onClick={() => { setShowForm(null); setForm({}); }}
                        style={{ padding: '9px 14px', background: '#F3F4F6',
                          border: 'none', borderRadius: 6, fontSize: 13, cursor: 'pointer' }}>
                        Отмена
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Кнопка подключить */}
              {!src.active && (
                <button onClick={() => setShowForm(isOpen ? null : src.key)}
                  style={{ width: '100%', padding: '9px', marginTop: 4,
                    background: isOpen ? '#F3F4F6' : src.bg,
                    color: isOpen ? '#6B7280' : src.color,
                    border: `1px solid ${isOpen ? '#E8EAF0' : src.color}`,
                    borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                  {isOpen ? '✕ Отмена' : connected.length > 0 ? '+ Добавить ещё' : '+ Подключить'}
                </button>
              )}

              {src.active && (
                <div style={{ fontSize: 12, color: src.color, fontWeight: 500, marginTop: 4 }}>
                  ✓ Всегда доступно — используйте вкладку «Загрузить прайс»
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Подключённые источники — сводная таблица */}
      {sources.length > 0 && (
        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          <div style={{ padding: '14px 20px', borderBottom: '1px solid #E8EAF0',
            fontWeight: 600, fontSize: 14 }}>
            Подключённые источники ({sources.length})
          </div>
          <table className="table">
            <thead>
              <tr>
                <th style={{ padding: '12px 20px' }}>Тип</th>
                <th>Название</th>
                <th>Поставщик</th>
                <th>Последняя проверка</th>
                <th style={{ textAlign: 'center' }}>Статус</th>
              </tr>
            </thead>
            <tbody>
              {sources.map(s => (
                <tr key={s.id}>
                  <td style={{ padding: '11px 20px' }}>
                    {SOURCE_TYPES.find(t => t.key === s.source_type)?.icon} {s.source_type}
                  </td>
                  <td style={{ fontWeight: 500, color: '#111827' }}>{s.name}</td>
                  <td style={{ color: '#8892A4', fontSize: 13 }}>{s.supplier || '—'}</td>
                  <td style={{ fontSize: 13, color: '#8892A4' }}>
                    {s.last_checked ? new Date(s.last_checked).toLocaleString('ru') : 'Ещё не проверялся'}
                  </td>
                  <td style={{ textAlign: 'center' }}>
                    <span className={s.is_active ? 'badge badge-success' : 'badge badge-warning'}>
                      {s.is_active ? '● Активен' : '⏸ Выкл'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
